import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { TextField, CircularProgress, InputAdornment } from '@mui/material'

import './CTextField.css'
import Icon from './Icon'

const CTextField = ({
    type = 'text',
    variant = 'outlined',
    required = false,
    error = false,
    disabled = false,
    autoFocus = false,
    isMultiline = false,
    iconType = 'far',
    colorTheme = 'generic',
    borderRadius = '6px',
    showLoader = false,
    toolTipFadeDelay = 500,
    multipleInputProps = [],
    className = '',
    noNumeric = false,
    ...props
}) => {
    const [value, setValue] = useState('')
    const [fieldStates, setFieldStates] = useState({
        className,
        label: props.label || "",
        placeholder: props.placeholder || "",
        helperText: props.helperText || "",
        disabled,
        error,
        required,
        autoComplete: props.autoComplete || '',
        isMultiline,
        inputRef: props.inputRef || null,
        autoFocus,
        type,
        value: props.value || "",
        valid: false,
        rightIcon: props.rightIcon || "",
        leftIcon: props.leftIcon || "",
        maxLength: props.maxLength || null,
        toolTipFadeDelay,
        showLoader,
        variant,
        iconType,
        multipleInputProps,
        borderRadius,
        colorTheme,
    })

    useEffect(() => {
        setFieldStates(prevState => ({
            ...prevState,
            className,
            label: props.label || "",
            placeholder: props.placeholder || "",
            helperText: props.helperText || "",
            disabled,
            error,
            required,
            autoComplete: props.autoComplete || '',
            isMultiline,
            inputRef: props.inputRef || null,
            autoFocus,
            type,
            value: props.value || "",
            rightIcon: props.rightIcon || "",
            leftIcon: props.leftIcon || "",
            maxLength: props.maxLength || null,
            toolTipFadeDelay,
            showLoader,
            variant,
            iconType,
            multipleInputProps,
            borderRadius,
            colorTheme,
        }))
    }, [className, props.label, props.placeholder, props.helperText, disabled, error, required, props.autoComplete, isMultiline, props.inputRef, autoFocus, type, props.value, props.rightIcon, props.leftIcon, props.maxLength, toolTipFadeDelay, showLoader, variant, iconType, multipleInputProps, borderRadius, colorTheme])

    useEffect(() => {
        if (props.value !== undefined) {
            setValue(props.value)
        }
    }, [props.value])

    const handleChange = (event) => {
        let newValue = event.target.value
        if (noNumeric) {
            newValue = newValue.replace(/[0-9]/g, '')
        }
        const syntheticEvent = newValue !== event.target.value
            ? { ...event, target: { ...event.target, value: newValue } }
            : event
        if (props.onChange) {
            props.onChange(syntheticEvent)
        }
        setValue(newValue)
    }

    const handleBlur = (event) => {
        const trimmedValue = event.target.value.trim()
        if (trimmedValue !== event.target.value) {
            setValue(trimmedValue)
            if (props.onChange) {
                props.onChange({ ...event, target: { ...event.target, value: trimmedValue } })
            }
        }
        if (props.onBlur) {
            props.onBlur(event)
        }
    }

    const handleFocus = (event) => {
        if (props.onFocus) {
            props.onFocus(event)
        }
    }

    return (
        <div className={`c-text-field color-theme-${fieldStates.colorTheme}`}>
            <TextField
                name={props.name}
                placeholder={fieldStates.placeholder}
                label={fieldStates.label}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                className={`c-text-field-input ${fieldStates.className}`}
                variant={fieldStates.variant}
                helperText={fieldStates.helperText}
                error={fieldStates.required && fieldStates.error}
                required={fieldStates.required}
                disabled={fieldStates.disabled}
                fullWidth={true}
                autoFocus={fieldStates.autoFocus}
                multiline={fieldStates.isMultiline}
                inputRef={fieldStates.inputRef}
                autoComplete={fieldStates.autoComplete}
                type={fieldStates.type}
                select={props.select}
                SelectProps={props.SelectProps}
                style={{ borderRadius: fieldStates.borderRadius }}
                inputProps={{
                    maxLength: fieldStates.maxLength
                }}
                InputProps={{
                    startAdornment: fieldStates.leftIcon && (
                        <InputAdornment position="start">
                            <Icon
                                name={fieldStates.leftIcon}
                                className='c-text-field-icon'
                                onClick={props.onLeftIconClick}
                            />

                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            {fieldStates.rightIcon && fieldStates.multipleInputProps?.length === 0 ? (
                                <Icon
                                    name={fieldStates.rightIcon}
                                    className='c-text-field-icon'
                                    onClick={props.onRightIconClick}
                                />
                            ) : (
                                <>
                                    {fieldStates.multipleInputProps?.length > 0 && (
                                        <>
                                            {!fieldStates.showLoader ? (
                                                <div className='multiple-input-props'>
                                                    {fieldStates.multipleInputProps.map((item, index) => (
                                                        <Icon
                                                            key={index}
                                                            name={item.icon}
                                                            onClick={item.clickEvent}
                                                            className='c-text-field-icon'
                                                        />
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className='loading-spinner'>
                                                    <CircularProgress color="inherit" thickness={5} size={20} />
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {fieldStates.showLoader && fieldStates.multipleInputProps?.length === 0 && (
                                        <div className='loading-spinner'>
                                            <CircularProgress color="inherit" thickness={5} size={20} />
                                        </div>
                                    )}
                                </>
                            )}
                        </InputAdornment>
                    ),
                }}
                onKeyDown={props.onKeyDown}
            >
                {props.children}
            </TextField>
        </div>
    )
}

CTextField.propTypes = {
    // Basic TextField props
    name: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    type: PropTypes.oneOf(['text', 'email', 'password', 'number', 'tel', 'url', 'search']),
    variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),

    // Validation props
    required: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,

    // State props
    disabled: PropTypes.bool,
    autoFocus: PropTypes.bool,
    isMultiline: PropTypes.bool,

    // Icon props
    leftIcon: PropTypes.string,
    rightIcon: PropTypes.string,
    iconType: PropTypes.oneOf(['far', 'fas', 'fal', 'fab']),
    onLeftIconClick: PropTypes.func,
    onRightIconClick: PropTypes.func,
    multipleInputProps: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.string.isRequired,
        clickEvent: PropTypes.func.isRequired
    })),

    // Styling props
    className: PropTypes.string,
    colorTheme: PropTypes.oneOf(['generic', 'primary', 'secondary', 'success', 'warning', 'error']),
    borderRadius: PropTypes.string,

    // Advanced props
    maxLength: PropTypes.number,
    showLoader: PropTypes.bool,
    autoComplete: PropTypes.string,
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

    // Select props
    select: PropTypes.bool,
    SelectProps: PropTypes.object,
    children: PropTypes.node,

    // Tooltip props
    toolTipFadeDelay: PropTypes.number,
    rightIconTooltip: PropTypes.string,

    // Input filtering
    noNumeric: PropTypes.bool,
}

export default CTextField